import { create } from 'zustand';

import { type AskQuestion, parseAskQuestions } from './ask-user';
import type { RunningJob } from './background-jobs';
import { deriveGoal, type GoalStatus } from './goal';
import { parseTodos, type TodoItem } from './todos';

export interface ToolCallState {
  id: string;
  name: string;
  input: unknown;
  output: string | null;
  /** Pictures the call made available: base64 data URLs from `browser_screenshot`, remote
   *  URLs from `web_fetch`. Carried beside `output` rather than inside it: a URL in the text
   *  would be unreadable to a human and would misreport what the call cost the model's
   *  context. */
  imageUrls: string[];
  isError: boolean;
  status: 'running' | 'done';
  /**
   * Announced by `tool_call_start` but not yet confirmed by `tool_call` — the model is still
   * streaming the call's arguments, so `input` is null.
   *
   * A placeholder is not a step that happened: the attempt behind it can be retried or abandoned,
   * and then the call never runs. Anything that ends an attempt drops the ones still unconfirmed
   * rather than leaving a row spinning at something that is not going to occur.
   */
  pending?: boolean;
}

export interface AgentTurn {
  id: string;
  /**
   * `job` is a background job's report: a user-role message on the model's side of the wire,
   * written by the agent loop rather than typed by anyone, so it renders as a notice and not as
   * something the user said. `goal` is a `/goal` note — the goal started, continued, paused or
   * ended — likewise the loop's, not anybody's words.
   */
  role: 'user' | 'assistant' | 'system' | 'job' | 'goal';
  content: string;
  /**
   * Reasoning the model streamed in its own channel, as `reasoning_delta` events.
   *
   * Separate from {@link AgentTurn.content} because it is separate on the wire: a model that
   * reasons in a dedicated field never puts it in the assistant text, so the `<think>`-scraping in
   * `stores/reasoning.ts` finds nothing and the block simply never appeared. Transient by design —
   * the backend does not persist reasoning, so a reloaded turn has none and falls back to scraping.
   */
  reasoning?: string;
  /** Base64 data URLs the user attached to this turn; kept for every turn that had them. */
  images?: string[];
  toolCalls: ToolCallState[];
  status: 'streaming' | 'done';
  /**
   * ISO timestamp, used only to interleave turns with the session's activity log
   * in real time order. Persisted turns carry the backend's value; a turn born
   * live is stamped on arrival, which is close enough for ordering against rows
   * written by the same events.
   */
  createdAt?: string;
}

/** An upstream call being retried after a 429/5xx, as reported by `retrying`. */
export interface RetryState {
  attempt: number;
  maxAttempts: number;
}

interface HistoryToolCallWire {
  id: string;
  name: string;
  input: unknown;
  output: string | null;
  imageUrls?: string[];
  /** Absent when the call succeeded. */
  isError?: boolean;
}

interface HistoryTurnWire {
  role: 'user' | 'assistant';
  content: string | null;
  images?: string[];
  toolCalls?: HistoryToolCallWire[];
  createdAt?: string;
  /** `job` marks a persisted background-job report, `goal` a goal note; both stored as user turns. */
  origin?: 'job' | 'goal';
}

/**
 * Where the socket is in the resume/attach sequence, as reported by the backend's
 * `attach_status` frames. The upgrade itself never blocks on the sandbox, so a socket can be
 * open and healthy while the sandbox behind it is still coming up.
 */
export type AttachState = 'connecting' | 'resuming' | 'ready' | 'unavailable';

/** A question the agent asked and stopped for. */
export interface PendingAsk {
  /** The `ask_user` call that has no result yet — the id the answer must be sent back with. */
  toolCallId: string;
  questions: AskQuestion[];
}

/** Token counts for one model call, or a session's running totals. */
interface AgentCallUsage {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
}

/** Raw `RpcEvent` shape relayed by the backend, plus our own synthetic `user_message`/`history`. */
export type AgentWireEvent =
  | { type: 'history'; turns: HistoryTurnWire[] }
  | { type: 'user_message'; content: string; images?: string[] }
  | { type: 'session_started'; sessionId: string; model: string }
  | { type: 'turn_started' }
  | { type: 'text_delta'; text: string }
  | { type: 'text'; text: string }
  | { type: 'reasoning_delta'; text: string }
  | { type: 'tool_call_start'; id: string; name: string }
  | { type: 'tool_call'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; id: string; output: string; isError: boolean; imageUrls?: string[] }
  | { type: 'ask_user_request'; toolCallId: string; questions: AskQuestion[] }
  | { type: 'approval_request'; id: string; toolCallId: string; tool: string; input: unknown }
  | {
      type: 'model_call';
      purpose: 'turn' | 'compaction' | 'skill_suggestion' | 'prewarm' | 'goal_judge';
      model: string;
      call: AgentCallUsage | null;
      contextTokens: number | null;
      session: AgentCallUsage & { costMicroUsd: number | null };
      durationMs: number;
      ttftMs?: number;
      finishReason: string;
      toolCallCount: number;
    }
  | { type: 'turn_complete'; aborted: boolean }
  | { type: 'goal_note'; content: string }
  | { type: 'goal'; status: GoalStatus | null }
  | { type: 'goal_rejected'; message: string }
  | { type: 'compaction'; method: 'summary' | 'elision'; tokensBefore: number; tokensAfter: number }
  | { type: 'jobs'; running: RunningJob[] }
  | { type: 'job_started'; id: string; command: string; logPath: string }
  | {
      type: 'job_finished';
      id: string;
      status: 'exited' | 'failed' | 'killed';
      exitCode?: number;
      durationMs: number;
      report?: string;
    }
  | { type: 'job_report'; ids: string[]; content: string }
  | { type: 'retrying'; attempt: number; maxAttempts: number; delayMs: number; message: string }
  | { type: 'error'; message: string }
  | { type: 'state'; sessionId: string; model: string; messageCount: number }
  | { type: 'attach_status'; state: Exclude<AttachState, 'connecting'>; detail: string | null }
  | { type: 'agent_exit' };

let nextTurnId = 0;
function newTurnId(): string {
  nextTurnId += 1;
  return `turn-${nextTurnId}`;
}

function lastAssistantTurn(turns: AgentTurn[]): AgentTurn | undefined {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i]!.role === 'assistant') return turns[i];
  }
  return undefined;
}

/**
 * Close out any turn still marked streaming.
 *
 * `turn_complete` is the happy path, but a turn can also end because the agent process died, the
 * socket dropped, or the run errored. Without settling here those turns stay `streaming` and
 * `turnInFlight` stays `true` forever — the permanently spinning composer.
 */
function settleTurns(turns: AgentTurn[]): AgentTurn[] {
  const streaming = turns.some((t) => t.status === 'streaming');
  const pending = turns.some((t) => t.toolCalls.some((tc) => tc.pending));
  if (!streaming && !pending) return turns;
  return turns.map((t) => ({
    ...t,
    status: t.status === 'streaming' ? ('done' as const) : t.status,
    // A placeholder the attempt never confirmed describes a call that will not happen. Keeping it
    // would leave a permanent spinner — the exact failure this whole signal exists to remove.
    toolCalls: t.toolCalls.some((tc) => tc.pending) ? t.toolCalls.filter((tc) => !tc.pending) : t.toolCalls,
  }));
}

/** Drop unconfirmed placeholders without settling the turn — the attempt restarts, the turn doesn't. */
function dropPendingCalls(turns: AgentTurn[]): AgentTurn[] {
  if (!turns.some((t) => t.toolCalls.some((tc) => tc.pending))) return turns;
  return turns.map((t) =>
    t.toolCalls.some((tc) => tc.pending) ? { ...t, toolCalls: t.toolCalls.filter((tc) => !tc.pending) } : t,
  );
}

/**
 * The `ask_user` call the agent stopped on, or null.
 *
 * Derived from the turns rather than tracked as live state, because a pending question is not
 * live: `ask_user` ends the turn on its way out, so the question survives the socket closing, the
 * loop being dropped and the sandbox hibernating. The condition is just "the newest turn is the
 * assistant's, and its last `ask_user` call has no result" — which is equally true of a turn
 * streamed live and one loaded from the `/turns` endpoint on a cold session, so both produce the
 * same card with no extra plumbing.
 *
 * Anything the user says afterwards lands as a new turn, which is what retires the question here:
 * it is only pending while it is still the last thing that happened, exactly as the server sees it.
 */
function derivePendingAsk(turns: AgentTurn[]): PendingAsk | null {
  const turn = turns[turns.length - 1];
  if (turn?.role !== 'assistant') return null;
  for (let i = turn.toolCalls.length - 1; i >= 0; i--) {
    const call = turn.toolCalls[i]!;
    if (call.name !== 'ask_user') continue;
    if (call.output !== null) return null;
    const questions = parseAskQuestions(call.input);
    return questions ? { toolCallId: call.id, questions } : null;
  }
  return null;
}

/**
 * The tool a `tool_result` belongs to.
 *
 * A result carries only the call's id, so anything reacting to *what finished* has to look the
 * name back up. Searches newest-first: ids are unique, and the match is almost always in the
 * turn still being streamed.
 */
export function findToolCallName(turns: AgentTurn[], toolCallId: string): string | null {
  for (let i = turns.length - 1; i >= 0; i--) {
    const call = turns[i]!.toolCalls.find((tc) => tc.id === toolCallId);
    if (call) return call.name;
  }
  return null;
}

/**
 * The current todo list: the most recent `set_todos` call across all turns that the agent loop
 * accepted. Its result decides — the loop refuses a list marking a step completed after an edit
 * nothing has checked since — so a call still waiting for its result, or one that errored, leaves
 * the previous list standing. Materialised so the pinned progress indicator can read it without
 * walking the whole thread on every render.
 */
function deriveTodos(turns: AgentTurn[]): TodoItem[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    const toolCalls = turns[i]!.toolCalls;
    for (let j = toolCalls.length - 1; j >= 0; j--) {
      const tc = toolCalls[j]!;
      if (tc.name !== 'set_todos' || !isAccepted(tc)) continue;
      const todos = parseTodos(tc.input);
      if (todos) return todos;
    }
  }
  return [];
}

function isAccepted(toolCall: ToolCallState): boolean {
  return toolCall.status === 'done' && toolCall.output !== null && !toolCall.isError;
}

/** Between one round's text and the next — the same separator the backend persists turns with. */
const ROUND_SEPARATOR = '\n\n';

/** `content` with `delta` added to the round starting at `roundStart`, which it may be opening. */
function withRoundDelta(content: string, roundStart: number, delta: string): string {
  const opensRound = roundStart > 0 && content.length === roundStart;
  const separator = opensRound ? ROUND_SEPARATOR : '';
  return content + separator + delta;
}

/** `content` with the round starting at `roundStart` replaced by that round's final `text`. */
function withRoundText(content: string, roundStart: number, text: string): string {
  const earlier = content.slice(0, roundStart);
  if (text === '') return earlier;
  const separator = roundStart > 0 ? ROUND_SEPARATOR : '';
  return earlier + separator + text;
}

/**
 * Everything materialised off `turns`, so no writer can update one and forget the other.
 * Deliberately not applied to `text`/`text_delta`: those fire per token and cannot change it.
 */
function withTurns(turns: AgentTurn[]) {
  return {
    turns,
    pendingAsk: derivePendingAsk(turns),
    todos: deriveTodos(turns),
    historyGoal: deriveGoal(turns),
  };
}

function systemTurn(content: string): AgentTurn {
  return {
    id: newTurnId(),
    role: 'system',
    content,
    toolCalls: [],
    status: 'done',
    createdAt: new Date().toISOString(),
  };
}

function goalTurn(content: string): AgentTurn {
  return { ...jobTurn(content), role: 'goal' };
}

function jobTurn(content: string): AgentTurn {
  return {
    id: newTurnId(),
    role: 'job',
    content,
    toolCalls: [],
    status: 'done',
    createdAt: new Date().toISOString(),
  };
}

function streamingAssistantTurn(): AgentTurn {
  return {
    id: newTurnId(),
    role: 'assistant',
    content: '',
    toolCalls: [],
    status: 'streaming',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Place a job report where the model saw it.
 *
 * The loop appends it to the transcript at the next model call, so it lands in one of three
 * places, and each wants the streamed output after it to keep landing on a bubble *below* it —
 * `lastAssistantTurn` always targets the newest assistant turn:
 * - **Idle**: appended. A `turn_started` for the turn the job wakes adds the bubble after it.
 * - **A turn that has produced nothing yet** (the one the job just woke, or a fresh prompt's):
 *   slotted in above that empty bubble, which keeps receiving the turn.
 * - **Between rounds of a turn with output**: that bubble is closed and a new one opened after the
 *   notice, so the rest of the turn reads as the agent's response to it.
 */
function placeJobReport(turns: AgentTurn[], report: AgentTurn): AgentTurn[] {
  const current = lastAssistantTurn(turns);
  if (current?.status !== 'streaming') return [...turns, report];

  const at = turns.indexOf(current);
  const before = turns.slice(0, at);
  const after = turns.slice(at + 1);
  const empty = !current.content && !current.reasoning && current.toolCalls.length === 0;
  if (empty) return [...before, report, current, ...after];
  return [...before, { ...current, status: 'done' }, report, streamingAssistantTurn(), ...after];
}

/**
 * Id carried by the optimistic first bubble rendered from `initial_prompt`, so
 * {@link AgentSessionState.hydrate} can recognise and replace it. Fixed rather
 * than generated because the whole point is to identify it later.
 */
const OPTIMISTIC_TURN_ID = 'optimistic-initial-prompt';

/** Map durable wire history into store turns. Shared by `hydrate` and the `history` event. */
function toTurns(wire: HistoryTurnWire[]): AgentTurn[] {
  return wire.map((t) => ({
    id: newTurnId(),
    role: t.origin ?? t.role,
    content: t.content ?? '',
    images: t.images ?? [],
    toolCalls: (t.toolCalls ?? []).map((tc) => ({
      id: tc.id,
      name: tc.name,
      input: tc.input,
      output: tc.output,
      imageUrls: tc.imageUrls ?? [],
      isError: tc.isError ?? false,
      status: 'done' as const,
    })),
    status: 'done' as const,
    createdAt: t.createdAt,
  }));
}

/**
 * A tool call waiting on the user. Emitted for anything that writes outside the sandbox —
 * connector write tools — and the turn is blocked until it is answered.
 */
export interface PendingApproval {
  /** Correlation id the agent expects back in `approval_response`. */
  id: string;
  toolCallId: string;
  tool: string;
  input: unknown;
}

interface AgentSessionState {
  connected: boolean;
  attachState: AttachState;
  attachDetail: string | null;
  turns: AgentTurn[];
  /** The question the agent stopped on, derived from {@link AgentSessionState.turns}. */
  pendingAsk: PendingAsk | null;
  /** Latest todo list, derived from the most recent `set_todos` tool call. */
  todos: TodoItem[];
  /** The goal as the loop last reported it, live. `null` until a `goal` event arrives. */
  liveGoal: GoalStatus | null;
  /** The goal the thread's goal notes describe — what a reload shows with no loop attached. */
  historyGoal: GoalStatus | null;
  sessionId: string | null;
  model: string | null;
  turnInFlight: boolean;
  /**
   * Where the streaming turn's current model round starts in its `content`. A turn runs several
   * rounds and each round's `text` event replaces only that round's deltas, so what the agent
   * said in earlier rounds stays. Reset whenever a streaming assistant turn is opened.
   */
  roundStart: number;
  /**
   * The upstream retry in progress, or null.
   *
   * Live-only state rather than a turn field: a retry is something happening *now*, and once the
   * call succeeds the transcript should carry no trace of it. Cleared by the next event that
   * proves the run moved on.
   */
  retry: RetryState | null;
  /** The tool call currently awaiting confirmation, if any. */
  pendingApproval: PendingApproval | null;
  /**
   * Background jobs still running in the sandbox, oldest first.
   *
   * Not cleared when the loop exits or the socket drops: a job outlives both, and a loop that
   * re-attaches re-announces the ones still running. Only `job_finished` removes one.
   */
  jobs: RunningJob[];

  setConnected: (connected: boolean) => void;
  setAttachState: (attachState: AttachState) => void;
  addUserTurn: (content: string, images?: string[]) => void;
  /** Settle a turn that ended without `turn_complete` (socket dropped, send failed). */
  endTurn: () => void;
  /** Settle a turn *and* explain why it stopped. */
  failTurn: (message: string) => void;
  /**
   * Record that a pending `ask_user` was answered, retiring the card.
   *
   * Explicit rather than event-driven, because no event carries this. The `ask_user` call is
   * deliberately left without a `tool_result` — that missing result *is* the pending question —
   * and the answer is written straight to the store by the endpoint that accepted it, on a turn
   * that has already ended. So the client that answered is the only thing that knows, until a
   * reload reads the result back off the persisted turn.
   */
  resolveAsk: (toolCallId: string, output: string) => void;
  /**
   * Seed the thread from durable sources.
   *
   * Persisted history always wins. With none yet, `initialPrompt` renders the
   * user's first message optimistically, so a freshly created session shows their
   * own words before the sandbox exists. That bubble is never additive — it is
   * replaced wholesale the moment real history arrives — so it cannot duplicate.
   */
  hydrate: (input: {
    history: HistoryTurnWire[] | null;
    initialPrompt: string | null;
    initialImages?: string[] | null;
  }) => void;
  handleEvent: (event: AgentWireEvent) => void;
  /** Clear the prompt once an answer has left the browser. */
  clearApproval: () => void;
  reset: () => void;
}

const initialState = {
  connected: false,
  attachState: 'connecting' as AttachState,
  attachDetail: null as string | null,
  turns: [] as AgentTurn[],
  pendingAsk: null as PendingAsk | null,
  todos: [] as TodoItem[],
  liveGoal: null as GoalStatus | null,
  historyGoal: null as GoalStatus | null,
  sessionId: null as string | null,
  model: null as string | null,
  turnInFlight: false,
  roundStart: 0,
  retry: null as RetryState | null,
  pendingApproval: null as PendingApproval | null,
  jobs: [] as RunningJob[],
};

export const useAgentSessionStore = create<AgentSessionState>((set) => ({
  ...initialState,

  setConnected: (connected) => set({ connected }),

  clearApproval: () => set({ pendingApproval: null }),

  setAttachState: (attachState) => set({ attachState, attachDetail: null }),

  // A settled turn can have no live approval: the agent is no longer listening for an answer,
  // so a card left on screen would be unanswerable.
  endTurn: () =>
    set((state) => ({
      turnInFlight: false,
      retry: null,
      pendingApproval: null,
      ...withTurns(settleTurns(state.turns)),
    })),

  failTurn: (message) =>
    set((state) => ({
      turnInFlight: false,
      retry: null,
      pendingApproval: null,
      ...withTurns([...settleTurns(state.turns), systemTurn(message)]),
    })),

  resolveAsk: (toolCallId, output) =>
    set((state) =>
      withTurns(
        state.turns.map((turn) =>
          turn.toolCalls.some((tc) => tc.id === toolCallId)
            ? {
                ...turn,
                toolCalls: turn.toolCalls.map((tc) =>
                  tc.id === toolCallId ? { ...tc, output, status: 'done' as const } : tc,
                ),
              }
            : turn,
        ),
      ),
    ),

  hydrate: ({ history, initialPrompt, initialImages }) =>
    set((state) => {
      const onlyOptimistic = state.turns.length === 1 && state.turns[0]!.id === OPTIMISTIC_TURN_ID;

      if (history && history.length > 0) {
        // Never clobber live/streamed turns — but the optimistic bubble is
        // exactly what this is meant to supersede.
        if (state.turns.length > 0 && !onlyOptimistic) return state;
        return withTurns(toTurns(history));
      }

      const prompt = initialPrompt?.trim();
      if (!prompt || state.turns.length > 0) return state;
      // Deliberately no streaming assistant turn alongside it: that would spin
      // forever if provisioning fails. The sandbox status line carries progress.
      return withTurns([
        {
          id: OPTIMISTIC_TURN_ID,
          role: 'user' as const,
          content: prompt,
          images: initialImages ?? [],
          toolCalls: [],
          status: 'done' as const,
          createdAt: new Date().toISOString(),
        },
      ]);
    }),

  addUserTurn: (content, images = []) => {
    set((state) => {
      const userTurn = {
        id: newTurnId(),
        role: 'user' as const,
        content,
        images,
        toolCalls: [],
        status: 'done' as const,
        createdAt: new Date().toISOString(),
      };
      // A turn already in flight makes this a queued follow-up: the backend won't start it
      // until the current turn finishes, so adding a streaming assistant bubble for it now
      // would steal the *current* turn's still-arriving text/tool events (`lastAssistantTurn`
      // always targets the newest one). Its own bubble appears when `turn_started` actually
      // fires for it.
      if (state.turnInFlight) return withTurns([...state.turns, userTurn]);
      return {
        ...withTurns([...state.turns, userTurn, streamingAssistantTurn()]),
        turnInFlight: true,
        roundStart: 0,
      };
    });
  },

  handleEvent: (event) => {
    switch (event.type) {
      case 'history': {
        // Never let an empty history wipe turns already on screen. A churning/reconnecting
        // socket or a paused sandbox (JSONL unreadable, DB race) can deliver an empty history;
        // replacing durable turns with `[]` is what made the thread go blank on reload. A
        // brand-new session is already empty, so skipping the set is a no-op there.
        if (event.turns.length === 0) return;
        set(withTurns(toTurns(event.turns)));
        return;
      }
      case 'user_message': {
        // A prompt that was already in flight when we (re)connected. Its whole job is to leave the
        // store with the prompt bubble followed by a streaming assistant turn to receive the
        // buffered/live events. Dedupe the user bubble: when the DB `history` we just applied
        // already ends with this exact prompt, don't add it again (that was the double-message
        // bug) — just append the streaming assistant turn.
        set((state) => {
          const last = state.turns[state.turns.length - 1];
          const userAlreadyPresent = last?.role === 'user' && last.content === event.content;
          const withUser = userAlreadyPresent
            ? state.turns
            : [
                ...state.turns,
                {
                  id: newTurnId(),
                  role: 'user' as const,
                  content: event.content,
                  images: event.images ?? [],
                  toolCalls: [],
                  status: 'done' as const,
                  createdAt: new Date().toISOString(),
                },
              ];
          return {
            ...withTurns([...withUser, streamingAssistantTurn()]),
            turnInFlight: true,
            roundStart: 0,
          };
        });
        return;
      }
      case 'session_started':
        set({ sessionId: event.sessionId, model: event.model });
        return;
      case 'turn_started': {
        // Fires for every turn, including one the backend started on its own by draining a
        // queued follow-up. If the composer already rendered this turn's assistant bubble
        // optimistically (the normal fresh-prompt path), the last turn is already streaming —
        // nothing to do. Otherwise this is a follow-up's turn actually starting: give it its
        // own bubble now so later text/tool events land on it instead of a stale one.
        set((state) => {
          const last = state.turns[state.turns.length - 1];
          if (last?.role === 'assistant' && last.status === 'streaming') return { turnInFlight: true, retry: null };
          return {
            turnInFlight: true,
            roundStart: 0,
            retry: null,
            ...withTurns([...state.turns, streamingAssistantTurn()]),
          };
        });
        return;
      }
      case 'state':
        set({ sessionId: event.sessionId, model: event.model });
        return;
      case 'text_delta': {
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          if (!turn) return state;
          return {
            // A token arriving is proof the retried call went through.
            retry: null,
            turns: state.turns.map((t) =>
              t.id === turn.id ? { ...t, content: withRoundDelta(t.content, state.roundStart, event.text) } : t,
            ),
          };
        });
        return;
      }
      case 'reasoning_delta': {
        // Kept beside the answer rather than concatenated into it: reasoning is not part of what
        // the model said, and folding it in would put it through the markdown renderer as prose.
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          if (!turn) return state;
          return {
            retry: null,
            turns: state.turns.map((t) =>
              t.id === turn.id ? { ...t, reasoning: (t.reasoning ?? '') + event.text } : t,
            ),
          };
        });
        return;
      }
      case 'text': {
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          if (!turn) return state;
          const content = withRoundText(turn.content, state.roundStart, event.text);
          return {
            roundStart: content.length,
            turns: state.turns.map((t) => (t.id === turn.id ? { ...t, content } : t)),
          };
        });
        return;
      }
      case 'tool_call_start': {
        // The row appears here rather than on `tool_call`, which only fires once the model has
        // finished streaming the call's arguments. For a `write` that is the whole file body —
        // a stretch with nothing on the wire, during which the thread looked hung.
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          if (!turn) return state;
          if (turn.toolCalls.some((tc) => tc.id === event.id)) return state;
          const placeholder: ToolCallState = {
            id: event.id,
            name: event.name,
            input: null,
            output: null,
            imageUrls: [],
            isError: false,
            status: 'running',
            pending: true,
          };
          return {
            retry: null,
            ...withTurns(
              state.turns.map((t) => (t.id === turn.id ? { ...t, toolCalls: [...t.toolCalls, placeholder] } : t)),
            ),
          };
        });
        return;
      }
      case 'tool_call': {
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          if (!turn) return state;
          // Confirms the placeholder in place when there is one, so the row keeps its position and
          // simply gains the arguments it was waiting for.
          const existing = turn.toolCalls.some((tc) => tc.id === event.id);
          const toolCall: ToolCallState = {
            id: event.id,
            name: event.name,
            input: event.input,
            output: null,
            imageUrls: [],
            isError: false,
            status: 'running',
          };
          return {
            retry: null,
            ...withTurns(
              state.turns.map((t) =>
                t.id !== turn.id
                  ? t
                  : {
                      ...t,
                      toolCalls: existing
                        ? t.toolCalls.map((tc) =>
                            tc.id === event.id ? { ...tc, name: event.name, input: event.input, pending: false } : tc,
                          )
                        : [...t.toolCalls, toolCall],
                    },
              ),
            ),
          };
        });
        return;
      }
      case 'tool_result': {
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          if (!turn) return state;
          const turns = state.turns.map((t) =>
            t.id === turn.id
              ? {
                  ...t,
                  toolCalls: t.toolCalls.map((tc) =>
                    tc.id === event.id
                      ? {
                          ...tc,
                          output: event.output,
                          imageUrls: event.imageUrls ?? [],
                          isError: event.isError,
                          status: 'done' as const,
                        }
                      : tc,
                  ),
                }
              : t,
          );
          return withTurns(turns);
        });
        return;
      }
      case 'model_call':
        // Deliberately a no-op: per-call tokens, cost and timing feed the CLI footer and its
        // traces; the web thread shows none of them.
        return;
      case 'ask_user_request':
        // Deliberately a no-op. The `tool_call` that precedes it already put the questions in the
        // turn with no result, which is what `derivePendingAsk` reads — and has to read, since a
        // question loaded from `/turns` on a cold session never had an event at all. Handled
        // explicitly rather than left to `default` so this stays a decision rather than an
        // oversight.
        return;
      case 'retrying': {
        // The distinction this exists for: an upstream 429 backs off for seconds with no other
        // event on the wire, which is indistinguishable from a hung agent unless we say so.
        // The retry restarts the request from scratch, so any call the failed attempt had begun
        // announcing is abandoned — and will be announced again, under a new id.
        set((state) => ({
          retry: { attempt: event.attempt, maxAttempts: event.maxAttempts },
          ...withTurns(dropPendingCalls(state.turns)),
        }));
        return;
      }
      case 'approval_request': {
        set({
          pendingApproval: {
            id: event.id,
            toolCallId: event.toolCallId,
            tool: event.tool,
            input: event.input,
          },
        });
        return;
      }
      case 'turn_complete': {
        set((state) => {
          const turn = lastAssistantTurn(state.turns);
          // Also drops any placeholder the turn ended without confirming — an aborted turn
          // abandons the call it had begun announcing.
          const turns = dropPendingCalls(state.turns);
          return {
            turnInFlight: false,
            retry: null,
            // The agent has stopped listening for an answer, so any card still on screen
            // could never be answered.
            pendingApproval: null,
            ...withTurns(turn ? turns.map((t) => (t.id === turn.id ? { ...t, status: 'done' as const } : t)) : turns),
          };
        });
        return;
      }
      case 'compaction': {
        const what = event.method === 'elision' ? 'Old tool outputs elided' : 'Conversation compacted';
        set((state) => ({
          ...withTurns([
            ...state.turns,
            {
              id: newTurnId(),
              role: 'system',
              content: `${what} (${event.tokensBefore.toLocaleString()} → ${event.tokensAfter.toLocaleString()} tokens).`,
              toolCalls: [],
              status: 'done',
            },
          ]),
        }));
        return;
      }
      case 'jobs': {
        // Sent on every attach, from the store: the authority on what is running now, including
        // jobs that ended while this client was away and whose `job_finished` it never saw.
        set({ jobs: event.running });
        return;
      }
      case 'job_started': {
        // Re-announced when a loop re-attaches to a job after a restart, so a known id keeps the
        // start time it was first seen with rather than resetting the elapsed clock.
        set((state) =>
          state.jobs.some((job) => job.id === event.id)
            ? state
            : { jobs: [...state.jobs, { id: event.id, command: event.command, startedAt: Date.now() }] },
        );
        return;
      }
      case 'job_finished': {
        // The report it carries is not rendered from here: `job_report` follows once the model is
        // actually sent it, and that is the position the thread shows it in.
        set((state) => ({ jobs: state.jobs.filter((job) => job.id !== event.id) }));
        return;
      }
      case 'job_report': {
        set((state) => withTurns(placeJobReport(state.turns, jobTurn(event.content))));
        return;
      }
      // A goal note lands between two iterations of one turn, where a job report would.
      case 'goal_note': {
        set((state) => withTurns(placeJobReport(state.turns, goalTurn(event.content))));
        return;
      }
      case 'goal': {
        set({ liveGoal: event.status });
        return;
      }
      // Not an `error`: a refused goal command leaves whatever turn is running untouched.
      case 'goal_rejected': {
        set((state) => withTurns([...state.turns, systemTurn(event.message)]));
        return;
      }
      case 'error': {
        // An errored run produces no `turn_complete`, so settle here or the composer spins on.
        set((state) => ({
          turnInFlight: false,
          retry: null,
          pendingApproval: null,
          ...withTurns([...settleTurns(state.turns), systemTurn(event.message)]),
        }));
        return;
      }
      case 'attach_status': {
        // Terminal for this socket: the sandbox couldn't be brought up, so nothing will ever
        // stream on it. Settle the thread rather than leaving a turn hanging.
        if (event.state === 'unavailable') {
          set((state) => ({
            attachState: 'unavailable',
            attachDetail: event.detail,
            turnInFlight: false,
            retry: null,
            ...withTurns(settleTurns(state.turns)),
          }));
          return;
        }
        set({ attachState: event.state, attachDetail: event.detail });
        return;
      }
      case 'agent_exit': {
        set((state) => ({
          attachState: 'unavailable',
          turnInFlight: false,
          retry: null,
          ...withTurns([...settleTurns(state.turns), systemTurn('The agent stopped unexpectedly. Reconnecting…')]),
        }));
        return;
      }
      default:
        return;
    }
  },

  reset: () => set({ ...initialState, turns: [], jobs: [] }),
}));

/** The session's goal: the live status when the loop has sent one, else what the thread records. */
export function selectGoal(state: Pick<AgentSessionState, 'liveGoal' | 'historyGoal'>): GoalStatus | null {
  return state.liveGoal ?? state.historyGoal;
}
